import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * SearchableSelect - Eine durchsuchbare Dropdown-Komponente
 * 
 * @param {Object[]} options - Array von {value, label, subtitle?} Objekten
 * @param {string} value - Aktuell ausgewählter Wert
 * @param {function} onValueChange - Callback wenn Wert sich ändert
 * @param {string} placeholder - Placeholder-Text wenn nichts ausgewählt
 * @param {string} searchPlaceholder - Placeholder für das Suchfeld
 * @param {string} emptyText - Text wenn keine Ergebnisse gefunden
 * @param {boolean} disabled - Deaktiviert die Komponente
 * @param {string} className - Zusätzliche CSS-Klassen für den Trigger
 * @param {string} dataTestId - Test-ID für Automation
 * @param {function} filterFn - Optionale custom Filter-Funktion (option, query) => boolean
 */
export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Auswählen...",
  searchPlaceholder = "Suchen...",
  emptyText = "Keine Ergebnisse gefunden.",
  disabled = false,
  className,
  dataTestId,
  filterFn,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Finde das ausgewählte Option-Objekt
  const selectedOption = options.find((opt) => opt.value === value);

  // Standard-Filterfunktion: sucht in label und subtitle
  const defaultFilterFn = (option, query) => {
    const q = query.toLowerCase();
    const labelMatch = option.label?.toLowerCase().includes(q);
    const subtitleMatch = option.subtitle?.toLowerCase().includes(q);
    return labelMatch || subtitleMatch;
  };

  // Verwende custom oder default Filter
  const filterFunction = filterFn || defaultFilterFn;

  // Gefilterte Optionen
  const filteredOptions = searchQuery
    ? options.filter((opt) => filterFunction(opt, searchQuery))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={dataTestId}
          className={cn(
            "w-full min-w-0 justify-between bg-input border-border text-foreground font-normal",
            disabled && "opacity-50 cursor-not-allowed",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate flex-1 min-w-0">
            {selectedOption ? (
              <span className="flex items-center gap-2 min-w-0">
                <span className="truncate">{selectedOption.label}</span>
                {selectedOption.subtitle && (
                  <span className="text-muted-foreground text-xs truncate">
                    ({selectedOption.subtitle})
                  </span>
                )}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] p-0 bg-popover border-border" 
        align="start"
        usePortal={false}
        onWheel={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="text-foreground"
          />
          <CommandList>
            <CommandEmpty className="text-muted-foreground">{emptyText}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onValueChange(option.value);
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span>{option.label}</span>
                    {option.subtitle && (
                      <span className="text-xs text-muted-foreground">
                        {option.subtitle}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
